import is from '@sindresorhus/is';
import clsx from 'clsx';
import type { FunctionComponent, ReactNode } from 'react';
import { useMemo, useState } from 'react';

import { useTranslate } from '~/shared/context/use-translate';
import { Button } from '~/ui/core/button/Button';
import { EmptyStateMessage } from '~/ui/core/empty-state-message/EmptyStateMessage';
import { IconCrossSmall } from '~/ui/core/icons';
import { ScrollArea } from '~/ui/core/scroll-area/ScrollArea';
import { SkeletonBase } from '~/ui/core/skeleton/Skeleton';
import { AnimatedTabs, TabsContent } from '~/ui/core/tabs/Tabs';
import { Tile, TileContent, TileControlGroup, tileContentEdgeOffsetClassNames } from '~/ui/core/tile/Tile';
import { HeaderContainer, NavigationButtons } from '~/ui/domain/Sidepanel/components/SidepanelHeader';

import type { AiTransformationItem } from '../../../data-contract/ai-transformation-strategy';
import type { AiTransformationFilters } from '../../../fetchers/position-management/ai-transformation-fetchers';
import { useUrlTileEscapeClose } from '../../../url-tiles';
import { QueryError } from '../../components/QueryError';
import type { StrategyGroupGrouping } from '../api/use-strategy-group-items';
import { useStrategyGroupItems } from '../api/use-strategy-group-items';
import { scoreLevelLabelKey } from '../model/score-levels';
import { statusLabelKey, statusPresentation } from '../model/statuses';

type GroupItemsQuery = ReturnType<typeof useStrategyGroupItems>;

/** A position or task of the group: its title and potential, then its status and where it sits. */
const ItemRow: FunctionComponent<{
    readonly isOpen: boolean;
    readonly item: AiTransformationItem;
    readonly onOpen: () => void;
}> = ({ isOpen, item, onOpen }) => {
    const translate = useTranslate();
    const { shortLabelKey } = statusPresentation(item.status);
    const context =
        item.subject === 'TASK'
            ? item.areas.map(({ title }) => title)
            : [item.area?.title, item.holderName].filter(is.string);

    return (
        <li className="list-none">
            <Button
                aria-current={isOpen}
                className={clsx(
                    'm-0 flex w-full flex-col items-stretch gap-xxs border-none bg-transparent px-m py-s text-left font-sans hover:bg-surface-hover',
                    isOpen && 'bg-surface-hover',
                )}
                data-item-id={item.id}
                data-testid="ai-transformation-strategy-group-row"
                type="button"
                variant="plain"
                onClick={onOpen}
            >
                <span className="flex w-full min-w-0 items-baseline justify-between gap-s">
                    <span className="truncate text-body-s font-medium text-text-primary">{item.title}</span>
                    <span className="shrink-0 text-body-xs text-text-secondary">
                        {translate(scoreLevelLabelKey(item.scoreLevel))}
                    </span>
                </span>
                <span className="flex w-full min-w-0 items-center gap-s text-body-xs text-text-secondary">
                    <span className="shrink-0" title={translate(statusLabelKey(item.status, item.subject))}>
                        {translate(shortLabelKey)}
                    </span>
                    {context.length === 0 ? null : <span className="truncate">{context.join(' · ')}</span>}
                </span>
            </Button>
        </li>
    );
};

/** The items of a paged group read, or its loading, error or empty state, with a button for the next page. */
const GroupItems: FunctionComponent<{
    readonly emptyKey: Parameters<ReturnType<typeof useTranslate>>[0];
    readonly errorKey: Parameters<ReturnType<typeof useTranslate>>[0];
    readonly loadingKey: Parameters<ReturnType<typeof useTranslate>>[0];
    readonly query: GroupItemsQuery;
    readonly renderItems: (items: readonly AiTransformationItem[]) => ReactNode;
}> = ({ emptyKey, errorKey, loadingKey, query, renderItems }) => {
    const translate = useTranslate();
    const items = useMemo(() => query.data?.pages.flatMap((page) => page.items) ?? [], [query.data]);

    if (query.isError) {
        return <QueryError isRetrying={query.isFetching} message={errorKey} onRetry={() => void query.refetch()} />;
    }
    if (query.data === undefined) {
        return (
            <div aria-label={translate(loadingKey)} className="flex flex-col gap-s" role="status">
                <SkeletonBase className="h-8 w-4/5 animate-none!" />
                <SkeletonBase className="h-40 w-full animate-none!" />
            </div>
        );
    }
    if (items.length === 0) return <EmptyStateMessage>{translate(emptyKey)}</EmptyStateMessage>;

    return (
        <>
            {renderItems(items)}
            {query.hasNextPage ? (
                <Button
                    className="self-center"
                    disabled={query.isFetching}
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                        if (!query.isFetching) void query.fetchNextPage();
                    }}
                >
                    {translate('common.show-more')}
                </Button>
            ) : null}
        </>
    );
};

const ItemList: FunctionComponent<{
    readonly items: readonly AiTransformationItem[];
    readonly onOpen: (item: AiTransformationItem) => void;
    readonly openId: string | undefined;
}> = ({ items, onOpen, openId }) => (
    <ul className="m-0 flex flex-col divide-y divide-border-soft p-0">
        {items.map((item) => (
            <ItemRow key={item.id} isOpen={item.id === openId} item={item} onOpen={() => onOpen(item)} />
        ))}
    </ul>
);

/** Items under a heading per level, in the order their levels first come. */
const byLevel = (items: readonly AiTransformationItem[]) => {
    const groups = new Map<string, { readonly title: string; readonly items: AiTransformationItem[] }>();
    for (const item of items) {
        const key = item.level?.id ?? '';
        const group = groups.get(key) ?? { items: [], title: item.level?.title ?? '' };
        group.items.push(item);
        groups.set(key, group);
    }
    return [...groups.entries()];
};

const Fact: FunctionComponent<{ readonly label: string; readonly value: ReactNode }> = ({ label, value }) => (
    <div className="flex min-w-0 items-baseline justify-between gap-m py-s">
        <dt className="shrink-0 text-body-s text-text-secondary">{label}</dt>
        <dd className="m-0 min-w-0 text-right text-body-s text-text-primary [overflow-wrap:anywhere]">{value}</dd>
    </div>
);

type GroupTab = 'overview' | 'positions';

/**
 * One role or level from the matrix: its overview, where it sits in the job architecture, its levels and the tasks its
 * positions hold, and its positions, under the page's filters but not its pick. A task opens the task's own tile, a
 * position the position's.
 */
export const StrategyGroupTile: FunctionComponent<{
    readonly architectureId: string;
    readonly filters: AiTransformationFilters;
    readonly groupId: string;
    readonly grouping: StrategyGroupGrouping;
    readonly onClose: () => void;
    readonly onOpenPosition: (positionId: string) => void;
    readonly onOpenTask: (taskId: string, version: number | undefined) => void;
    readonly openPositionId: string | undefined;
    readonly openTaskId: string | undefined;
}> = ({
    architectureId,
    filters,
    groupId,
    grouping,
    onClose,
    onOpenPosition,
    onOpenTask,
    openPositionId,
    openTaskId,
}) => {
    const translate = useTranslate();
    const [tab, setTab] = useState<GroupTab>('overview');
    const positions = useStrategyGroupItems({ architectureId, filters, groupId, grouping });
    const tasks = useStrategyGroupItems({ architectureId, filters, groupId, grouping, subject: 'TASK' });
    const firstPage = positions.data?.pages[0];
    const first = firstPage?.items[0];
    const title =
        (grouping === 'ROLE' ? first?.role : first?.level)?.title ??
        translate(
            grouping === 'ROLE'
                ? 'pages.ai-transformation.strategy.group-tile.role'
                : 'pages.ai-transformation.strategy.group-tile.level',
        );
    const placement = [first?.jobFamily?.title, first?.jobCluster?.title].filter(is.string);
    const levels = useMemo(
        () =>
            grouping === 'ROLE'
                ? byLevel(positions.data?.pages.flatMap((page) => page.items) ?? []).map(([id, group]) => ({
                      count: group.items.length,
                      id,
                      title: group.title,
                  }))
                : [],
        [grouping, positions.data],
    );
    const taskTotal = tasks.data?.pages[0]?.total;

    useUrlTileEscapeClose(onClose);

    const openTask = (item: AiTransformationItem) => onOpenTask(item.id, item.taskVersion ?? undefined);
    const openPosition = (item: AiTransformationItem) => onOpenPosition(item.id);

    return (
        <Tile data-testid="ai-transformation-strategy-group-tile">
            <ScrollArea
                className="min-w-0 flex-1"
                scrollFade="vertical"
                scrollFadeSize="var(--spacing-4xl)"
                viewportClassName="[&>div]:block!"
            >
                <TileContent className="min-h-full w-full">
                    <div className="flex w-full flex-1 flex-col gap-l">
                        <div className={clsx('bg-surface-raised pb-m pt-tile-edge', tileContentEdgeOffsetClassNames)}>
                            <HeaderContainer>
                                <NavigationButtons>
                                    <TileControlGroup>
                                        <Button
                                            aria-label={translate('common.close')}
                                            iconOnly={true}
                                            size="sm"
                                            variant="secondary"
                                            onClick={onClose}
                                        >
                                            <IconCrossSmall />
                                        </Button>
                                    </TileControlGroup>
                                </NavigationButtons>
                            </HeaderContainer>
                        </div>
                        <div className="flex min-w-0 flex-col gap-xxs">
                            <h2 className="m-0 text-h3 text-text-heading [overflow-wrap:anywhere]">{title}</h2>
                            {placement.length === 0 ? null : (
                                <span className="text-body-s text-text-secondary">{placement.join(' · ')}</span>
                            )}
                        </div>
                        <AnimatedTabs
                            tabs={[
                                {
                                    label: translate('pages.ai-transformation.strategy.group-tile.overview'),
                                    value: 'overview',
                                },
                                {
                                    label:
                                        firstPage === undefined
                                            ? translate('pages.ai-transformation.strategy.view.positions')
                                            : `${translate('pages.ai-transformation.strategy.view.positions')} · ${firstPage.total}`,
                                    value: 'positions',
                                },
                            ]}
                            value={tab}
                            onValueChange={(value) => setTab(value === 'positions' ? 'positions' : 'overview')}
                        >
                            <TabsContent className="mt-l flex min-w-0 flex-col gap-l" value="overview">
                                <dl className="m-0 flex flex-col divide-y divide-border-soft">
                                    {first?.jobFamily ? (
                                        <Fact
                                            label={translate('pages.ai-transformation.strategy.filter.job-family')}
                                            value={first.jobFamily.title}
                                        />
                                    ) : null}
                                    {first?.jobCluster ? (
                                        <Fact
                                            label={translate('pages.ai-transformation.strategy.filter.job-cluster')}
                                            value={first.jobCluster.title}
                                        />
                                    ) : null}
                                    {levels.length === 0 ? null : (
                                        <Fact
                                            label={translate('pages.ai-transformation.strategy.view.levels')}
                                            value={levels.map((level) => `${level.title} (${level.count})`).join(', ')}
                                        />
                                    )}
                                    {firstPage === undefined ? null : (
                                        <Fact
                                            label={translate('pages.ai-transformation.strategy.view.positions')}
                                            value={firstPage.total}
                                        />
                                    )}
                                </dl>
                                <section className="flex min-w-0 flex-col gap-s">
                                    <h3 className="m-0 text-body font-medium text-text-heading">
                                        {taskTotal === undefined
                                            ? translate('pages.ai-transformation.strategy.view.tasks')
                                            : `${translate('pages.ai-transformation.strategy.view.tasks')} · ${taskTotal}`}
                                    </h3>
                                    <GroupItems
                                        emptyKey="pages.ai-transformation.strategy.group-tile.tasks-empty"
                                        errorKey="pages.ai-transformation.strategy.group-tile.tasks-error"
                                        loadingKey="pages.ai-transformation.strategy.group-tile.loading"
                                        query={tasks}
                                        renderItems={(items) => (
                                            <ItemList items={items} openId={openTaskId} onOpen={openTask} />
                                        )}
                                    />
                                </section>
                            </TabsContent>
                            <TabsContent className="mt-l flex min-w-0 flex-col gap-m" value="positions">
                                <GroupItems
                                    emptyKey="pages.ai-transformation.strategy.group-tile.empty"
                                    errorKey="pages.ai-transformation.strategy.group-tile.error"
                                    loadingKey="pages.ai-transformation.strategy.group-tile.loading"
                                    query={positions}
                                    renderItems={(items) =>
                                        grouping === 'ROLE' ? (
                                            byLevel(items).map(([levelId, group]) => (
                                                <section key={levelId} className="flex min-w-0 flex-col gap-xxs">
                                                    {group.title === '' ? null : (
                                                        <h3 className="m-0 px-m text-body-xs font-medium text-text-secondary">
                                                            {`${group.title} · ${group.items.length}`}
                                                        </h3>
                                                    )}
                                                    <ItemList
                                                        items={group.items}
                                                        openId={openPositionId}
                                                        onOpen={openPosition}
                                                    />
                                                </section>
                                            ))
                                        ) : (
                                            <ItemList items={items} openId={openPositionId} onOpen={openPosition} />
                                        )
                                    }
                                />
                            </TabsContent>
                        </AnimatedTabs>
                    </div>
                </TileContent>
            </ScrollArea>
        </Tile>
    );
};
